import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import thunk from 'redux-thunk';
import reducers from './reducers';
import './index.css';

import reportWebVitals from './reportWebVitals';
import { configureStore } from '@reduxjs/toolkit';
import { Provider } from 'react-redux';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import '../node_modules/bootstrap/scss/bootstrap.scss';
import '../node_modules/font-awesome/scss/font-awesome.scss';
import './assets/assets/scss/main.scss';
import './assets/assets/scss/color_skins.scss';
// BHP-1038: Tailwind entry, loaded last so its utilities win over Bootstrap.
import './styles/tailwind.css';
import QueryProvider from './shared/query/QueryProvider';
import AuthConfigProvider from './components/Auth/AuthConfigProvider';
import UserProvider from './components/Auth/UserProvider';
import Authorized from './routes/authorized';
import AuthCheckProvider from './components/Auth/AuthCheckProvider';
// BHP-1070: Backend2.0 sign-up onboarding replaces the legacy CustomerProvider.
// OnboardingGate bootstraps the account (GET /api/accounts — also the BHP-1069
// provisioning trigger) and routes an account without a customer to onboarding.
import OnboardingGate from './features/account/ui/OnboardingGate';
import Onboarding from './features/customer/ui/Onboarding';
// BHP-1053: load the active customer (Backend2.0) and mirror it into the legacy
// customer slice so Layout/navbar have a customer context.
import ActiveCustomerProvider from './features/customer/ui/ActiveCustomerProvider';
// BHP-1191: the public payment-result page, reached from Mollie via the
// backend (BHP-1190).
import PaymentResult from './features/payment/ui/PaymentResult';

// @ts-expect-error window object not defined
window.__DEV__ = true;

const store = configureStore({
  reducer: reducers,
  middleware: [thunk],
  devTools: true
});

// Set user theme
document.body.classList.add('theme-cyan');

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// BHP-1191: the payment-result page is decided here, before any of the auth
// providers below it, and not by a <Route>.
//
// It has to be: the customer clicked a button in an invoice mail and is not
// logged in, so AuthCheckProvider would hand them UnAuthorized, whose
// catch-all sends everything to /login — the sign-in screen this page exists
// to avoid. Putting it in both route tables would work, but it would still
// render behind `auth.isLoading`, and someone who has just paid must not be
// looking at a spinner because Authentik is slow to answer.
//
// A <Route> at the top is not an option either: AuthCheckProvider brings its
// own <BrowserRouter>, and React Router refuses to nest two.
const isPaymentResult = window.location.pathname.startsWith('/betaling');

root.render(
  <React.StrictMode>
    {/* BHP-1044: QueryProvider wraps the app for React Query server-state.
            The Redux Provider stays until every slice is migrated off Redux. */}
    <QueryProvider>
      {isPaymentResult ? (
        <PaymentResult />
      ) : (
        <Provider store={store}>
          <AuthConfigProvider>
            <AuthCheckProvider>
              <UserProvider>
                <BrowserRouter>
                  <OnboardingGate onboarding={<Onboarding />}>
                    <ActiveCustomerProvider>
                      <Authorized />
                    </ActiveCustomerProvider>
                  </OnboardingGate>
                </BrowserRouter>
              </UserProvider>
            </AuthCheckProvider>
          </AuthConfigProvider>
        </Provider>
      )}
    </QueryProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
